import { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Divider,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/NotificationsNone';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import type { AlertRow } from 'shared';
import { api } from '../api';
import { formatRelative } from '../format';
import { tokens } from '../theme';

/**
 * Chuông cảnh báo — spec SC-08 §5.
 *
 * Nơi nhận cảnh báo là ĐÂY, không phải email: chưa có SMTP nội bộ (spec SC-09 §7
 * câu hỏi 7). Spec đã chốt như vậy, nên đừng thêm dòng "sẽ gửi email" ở đâu.
 *
 * Hỏi lại mỗi 60 giây. Không dùng WebSocket cho một con số đổi vài lần mỗi tuần —
 * đó là cả một tầng hạ tầng phải nuôi, cho một thứ chậm hơn một phút cũng không
 * ai nhận ra.
 */
const NHIP_HOI_MS = 60_000;

/**
 * Dùng `formatRelative` dùng chung thay vì tự tính: nó đã xử lý việc trường ngày
 * từ API là CHUỖI chứ không phải `Date` (xem ghi chú ở `format.ts`), và nó cho ra
 * cùng cách nói với cột "Quét lần cuối" ở màn danh sách.
 */
function ThoiDiem({ at }: { at: Date }) {
  return (
    <Typography sx={{ fontSize: 11.5, color: tokens.faint, flex: 'none' }}>
      {formatRelative(at)}
    </Typography>
  );
}

function Dong({ row, onOpen }: { row: AlertRow; onOpen: () => void }) {
  const { tu, den, giam, mucMoiHong } = row.detail;

  return (
    <ButtonBase
      component={RouterLink}
      to={`/pages/${row.pageSlug}`}
      onClick={onOpen}
      sx={{
        display: 'block',
        textAlign: 'left',
        width: '100%',
        px: 2,
        py: 1.3,
        borderLeft: `3px solid ${row.readAt === null ? tokens.rose : 'transparent'}`,
        bgcolor: row.readAt === null ? tokens.roseBg : 'transparent',
        '&:hover': { bgcolor: tokens.rowHover },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="baseline">
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, flex: 1 }}>{row.pageCode}</Typography>
        <ThoiDiem at={row.createdAt} />
      </Stack>
      <Typography sx={{ fontSize: 12.5, color: tokens.roseText, fontWeight: 600, mt: 0.2 }}>
        Tụt {giam} điểm ({tu} → {den})
      </Typography>
      {/* Đây là phần có giá trị nhất của cảnh báo: "tụt 15 điểm" không nói cho
          người phụ trách biết phải làm gì, "Meta Title vừa hỏng" thì nói được. */}
      {mucMoiHong.length > 0 && (
        <Typography sx={{ fontSize: 12.5, color: tokens.muted, mt: 0.2 }}>
          Vừa hỏng: {mucMoiHong.map((m) => m.label).join(', ')}
        </Typography>
      )}
    </ButtonBase>
  );
}

export function AlertBell() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['alerts'],
    queryFn: () => api.alerts(false),
    refetchInterval: NHIP_HOI_MS,
  });

  const docHet = useMutation({
    mutationFn: () => api.alertDaDocHet(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts'] }),
  });
  const docMot = useMutation({
    mutationFn: (id: number) => api.alertDaDoc(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts'] }),
  });

  const soChuaDoc = data?.soChuaDoc ?? 0;
  const rows = data?.rows ?? [];

  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={soChuaDoc > 0 ? `${soChuaDoc} cảnh báo chưa đọc` : 'Cảnh báo'}
        sx={{ borderRadius: '50%', p: 0.7, color: '#CFE2E8' }}
      >
        <Badge
          badgeContent={soChuaDoc}
          color="error"
          slotProps={{ badge: { sx: { fontSize: 10, height: 17, minWidth: 17 } } }}
        >
          <NotificationsIcon sx={{ fontSize: 21 }} />
        </Badge>
      </ButtonBase>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 370, maxHeight: 460, mt: 0.5 } } }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.2 }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Cảnh báo</Typography>
          {soChuaDoc > 0 && (
            <Button size="small" onClick={() => docHet.mutate()} disabled={docHet.isPending}>
              Đánh dấu đã đọc hết
            </Button>
          )}
        </Stack>
        <Divider />

        {isLoading && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {!isLoading && rows.length === 0 && (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography sx={{ fontSize: 13, color: tokens.muted }}>
              Chưa có cảnh báo nào.
            </Typography>
            {/* Danh sách trống KHÔNG có nghĩa là mọi thứ đều ổn — nói rõ điều kiện,
                cùng lý do với dải ghi chú ở màn Rà soát (spec SC-19 §4 Y6). */}
            <Typography sx={{ fontSize: 12, color: tokens.faint, mt: 0.5 }}>
              Cảnh báo sinh ra khi một trang tụt điểm quá ngưỡng ở lần quét gần nhất.
            </Typography>
          </Box>
        )}

        {rows.map((r) => (
          <Box key={r.id}>
            <Dong
              row={r}
              onOpen={() => {
                setAnchor(null);
                if (r.readAt === null) docMot.mutate(r.id);
              }}
            />
            <Divider />
          </Box>
        ))}
      </Popover>
    </>
  );
}
